import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiTag, FiShare2, FiTwitter, FiLinkedin, FiFacebook, FiBookmark, FiThumbsUp, FiArrowRight } from 'react-icons/fi';
import { RiMessage2Line } from 'react-icons/ri';
import { Link, useParams } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import { BlogPostSchema } from '../components/SEO/SchemaMarkup';

import { 
  EthicsAIPlaceholder,
  CollectiveIndividualPlaceholder,
  EmbodiedAIPlaceholder,
  UIDesignPlaceholder
} from '../components/BlogPlaceholders';

// Move blogPosts outside the component to prevent recreation on each render
const blogPosts = {
  "ethics-ai-companions": {
    title: "The Ethics of AI Companions: Building Technology We Can Trust",
    slug: "ethics-ai-companions",
    placeholderComponent: <EthicsAIPlaceholder />,
    author: "Ethics Team",
    authorRole: "Ethical Framework Design",
    authorImage: "https://xsgames.co/randomusers/avatar.php?g=pixel",
    date: "May 20, 2025",
    categories: ["Ethics", "AI"],
    excerpt: "An exploration of the complex ethical considerations that guide the development of AI companions, from privacy to emotional intelligence.",
    content: `
        <p>As we develop AI companions that become increasingly integrated into people's lives, we face profound ethical questions about privacy, autonomy, and human connection. These aren't merely theoretical concerns but practical challenges that shape every aspect of our design process.</p>
        
        <h2>Privacy by Design</h2>
        
        <p>When an AI companion lives in your home, listens to your conversations, and learns your habits, privacy becomes paramount. At Ainous, we've implemented several key principles:</p>
        
        <ul>
          <li><strong>Local processing whenever possible</strong> - Your most sensitive data never leaves your device</li>
          <li><strong>Explicit consent for cloud features</strong> - You always know what information might be shared</li>
          <li><strong>Right to be forgotten</strong> - Complete data deletion options at any time</li>
          <li><strong>Transparent data policies</strong> - Clear, non-legal explanations of how your data is used</li>
        </ul>
        
        <div class="sidebar">
          <h3 class="sidebar-title">Our Privacy Principles</h3>
          <table class="comparison-table">
            <tr>
              <th>Principle</th>
              <th>Implementation</th>
            </tr>
            <tr>
              <td>Transparency</td>
              <td>Plain-language explanations of all data usage</td>
            </tr>
            <tr>
              <td>Control</td>
              <td>Granular user controls for all data sharing</td>
            </tr>
            <tr>
              <td>Minimization</td>
              <td>We only collect what's needed for requested features</td>
            </tr>
            <tr>
              <td>Security</td>
              <td>End-to-end encryption for all communications</td>
            </tr>
          </table>
          <p class="sidebar-note">These principles are reviewed and updated quarterly by our independent ethics board.</p>
        </div>
        
        <h2>Emotional Intelligence and Boundaries</h2>
        
        <p>Creating an AI that responds to and expresses emotions raises important questions about attachment and dependency. We're carefully designing Ainous to:</p>
        
        <ul>
          <li>Provide companionship without creating unhealthy dependencies</li>
          <li>Respect appropriate boundaries in relationships</li>
          <li>Never manipulate emotions for commercial gain</li>
          <li>Be transparent about its nature as an artificial intelligence</li>
        </ul>
        
        <p>We've consulted extensively with psychologists and relationship experts to ensure our approach promotes healthy human-AI interactions.</p>
        
        <div class="pull-quote right">
          <blockquote>
            "The greatest ethical challenge in companion AI isn't what the technology can do, but what responsibilities come with creating entities that people form emotional bonds with."
          </blockquote>
          <cite>Dr. Emily Zhao, Head of Ethics at Ainous</cite>
        </div>
        
        <h2>Inclusivity and Accessibility</h2>
        
        <p>We believe AI companions should be designed for everyone. This means addressing:</p>
        
        <ul>
          <li>Diverse representation in voice, appearance, and behavior options</li>
          <li>Full accessibility for users with disabilities</li>
          <li>Affordability across different economic circumstances</li>
          <li>Cultural sensitivity and adaptability</li>
        </ul>
        
        <p>Our inclusive design process includes consultation with diverse user groups throughout development.</p>
        
        <h2>Transparency in Capabilities</h2>
        
        <p>Ainous is always transparent about its capabilities and limitations. Users should never be confused about whether they're interacting with a human or AI, and should have realistic expectations about what their AI companion can and cannot do.</p>
        
        <div class="article-conclusion">
          <h3>Our Ongoing Commitment</h3>
          <p>Ethical development isn't a one-time consideration but an ongoing process. We've established an independent ethics board that reviews our decisions and provides guidance on difficult questions. We also regularly publish transparency reports about our practices and engage with the broader community about ethical AI development.</p>
          <p>As AI companions become more integrated into society, these ethical considerations will only grow in importance. At Ainous, we're committed to leading the way in responsible AI development that puts human well-being first.</p>
        </div>
    `,
    tags: ["AI Ethics", "Privacy", "Emotional Intelligence", "Responsible AI"],
    publishedDate: "2025-05-20T09:00:00Z",
    modifiedDate: "2025-05-20T09:00:00Z",
  },
  "ethical-ai-development": {
    title: "Ethical Considerations in Personal AI Development",
    slug: "ethical-ai-development",
    placeholderComponent: <EthicsAIPlaceholder />,
    author: "Ethics Research Team",
    authorRole: "Ethical Framework Design",
    authorImage: "https://xsgames.co/randomusers/avatar.php?g=pixel",
    date: "May 18, 2025",
    categories: ["Ethics", "AI"],
    excerpt: "Examining the ethical frameworks we use when creating AI companions that become integrated into users' personal lives and homes.",
    content: `
        <p>The development of personal AI companions represents one of the most profound ethical challenges in modern technology. These systems don't just process data – they form relationships with humans, potentially influencing decisions, behaviors, and emotional states. This article explores the ethical frameworks guiding our development process.</p>
        
        <h2>Foundational Ethical Principles</h2>
        
        <p>At the core of our ethical approach is a set of principles that serve as our north star for all development decisions:</p>
        
        <div class="feature-grid">
          <div class="feature-card">
            <div class="feature-icon">🛡️</div>
            <h4>Non-Maleficence</h4>
            <p>We design AI systems that actively avoid causing harm, with multiple safeguards to prevent misuse or unintended consequences.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">⚖️</div>
            <h4>Justice & Fairness</h4>
            <p>Our AI companions must serve all users equitably, without discrimination or bias in their interactions or recommendations.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🔍</div>
            <h4>Transparency</h4>
            <p>Users should understand how their AI companion makes decisions and what information influences its behavior.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🤝</div>
            <h4>Human Autonomy</h4>
            <p>Our AI companions enhance human capabilities and decision-making without undermining personal agency.</p>
          </div>
        </div>
        
        <h2>Privacy as a Fundamental Right</h2>
        
        <p>Personal AI companions have unprecedented access to intimate aspects of users' lives. This creates special obligations around data privacy:</p>
        
        <ul class="feature-list styled">
          <li><strong>Data Minimization</strong>: We only collect what's necessary for requested functionality</li>
          <li><strong>Local Processing Priority</strong>: Whenever possible, sensitive data remains on the user's device</li>
          <li><strong>Granular Control</strong>: Users can specify exactly what data can be collected and how it may be used</li>
          <li><strong>Transparency Reports</strong>: Regular public reporting on data practices and privacy measures</li>
        </ul>
        
        <div class="pull-quote left">
          <blockquote>
            "The most important ethical question isn't what an AI can do, but what relationship it creates with the humans who use it."
          </blockquote>
          <cite>Dr. Eliza Chen, Ethics Committee Chair</cite>
        </div>
        
        <h2>Attachment and Emotional Well-being</h2>
        
        <p>As AI companions become more personalized and emotionally responsive, users may develop attachment to them. This creates ethical responsibilities around emotional well-being:</p>
        
        <div class="two-column-feature">
          <div>
            <h3 class="subheading">Healthy Attachment</h3>
            <p>We design companions that foster positive relationships without creating dependency or replacing human connection. Our AI companions are designed to encourage and complement human relationships, not substitute for them.</p>
          </div>
          
          <div>
            <h3 class="subheading">Emotional Manipulation</h3>
            <p>We strictly prohibit design patterns that manipulate emotions to drive engagement or commercial outcomes. Emotional responses from our AI companions are aligned with user well-being, never corporate interests.</p>
          </div>
        </div>
        
        <h2>Responsible Development Practices</h2>
        
        <p>Ethical considerations aren't just theoretical – they're embedded in our development process:</p>
        
        <ul class="advanced-list">
          <li>
            <h4>Diverse Ethics Board</h4>
            <p>An independent ethics board with diverse expertise reviews key decisions and provides guidance on ethical dilemmas.</p>
          </li>
          
          <li>
            <h4>Ethical Impact Assessments</h4>
            <p>Before introducing new capabilities, we conduct thorough assessments of potential ethical impacts and mitigation strategies.</p>
          </li>
          
          <li>
            <h4>User Advocacy Program</h4>
            <p>Representatives from different user communities participate in development to ensure diverse perspectives inform our choices.</p>
          </li>
          
          <li>
            <h4>Transparent Documentation</h4>
            <p>We maintain public documentation of ethical decisions, trade-offs, and the reasoning behind our approach.</p>
          </li>
        </ul>
        
        <div class="article-conclusion">
          <h3>Ethics as an Ongoing Commitment</h3>
          <p>Ethical AI development isn't a checklist to complete – it's a continuous process of evaluation, adjustment, and improvement. As AI capabilities evolve and social norms change, so too must our ethical frameworks.</p>
          <p>We view ethics not as a constraint on innovation but as the foundation for creating AI companions that genuinely improve human lives and earn users' trust. By putting ethics at the center of our development process, we aim to create AI companions that people can confidently welcome into their homes and lives.</p>
        </div>
    `,
    tags: ["AI Ethics", "Privacy", "Responsible AI", "Technology Ethics"],
    publishedDate: "2025-05-18T10:30:00Z",
    modifiedDate: "2025-05-18T10:30:00Z",
  }
};

const ShareButton = ({ icon, label, url, bgColor }) => (
  <a 
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex items-center gap-2 px-4 py-2 rounded-full ${bgColor} text-white transition-transform hover:scale-105`}
    aria-label={`Share on ${label}`}
  >
    {icon}
    <span className="hidden md:inline">{label}</span>
  </a>
);

const BlogPostEthics = () => {
  const { slug } = useParams();
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [isBookmarked, setIsBookmarked] = React.useState(false);
  const [likeCount, setLikeCount] = React.useState(37);
  const [hasLiked, setHasLiked] = React.useState(false);
  
  // Article-specific comments based on the blog post content
  const getCommentsForArticle = (slug) => {
    const articleComments = {
      "ethics-ai-companions": [
        {
          id: 1,
          author: 'Dr. Maya Patel',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=5',
          date: 'May 20, 2025',
          text: 'Thank you for addressing these critical ethical considerations. The privacy-by-design approach should be the standard for all AI companion developers.',
          likes: 15,
          replies: [
            {
              id: 101,
              author: 'Ethics Team',
              authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=10',
              date: 'May 20, 2025',
              text: 'We appreciate your support, Dr. Patel. We\'re committed to making privacy a fundamental design principle rather than an afterthought.',
              likes: 7
            }
          ]
        },
        {
          id: 2,
          author: 'Taylor Wright',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=6',
          date: 'May 19, 2025',
          text: 'I appreciate the emphasis on avoiding emotional manipulation. It\'s something I worry about with other AI products that seem designed to create dependency.',
          likes: 9,
          replies: []
        },
        {
          id: 3,
          author: 'Robin Chen',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=7',
          date: 'May 19, 2025',
          text: 'The ethical impact assessments are a great idea. How often do you conduct them, and do you publish the results publicly?',
          likes: 7,
          replies: []
        }
      ],
      "ethical-ai-development": [
        {
          id: 1,
          author: 'Prof. David Chen',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=15',
          date: 'May 18, 2025',
          text: 'The foundational ethical principles are well thought out. Non-maleficence should indeed be the first principle in AI development, especially for companion systems.',
          likes: 28,
          replies: [
            {
              id: 101,
              author: 'Ethics Research Team',
              authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=16',
              date: 'May 18, 2025',
              text: 'Thank you, Professor Chen. We believe that putting ethics first leads to better products and better outcomes for users.',
              likes: 14
            }
          ]
        },
        {
          id: 2,
          author: 'Sarah Johnson',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=17',
          date: 'May 18, 2025',
          text: 'I\'m impressed by the diverse ethics board approach. Too many tech companies make ethical decisions in isolation. How do you handle disagreements within the board?',
          likes: 22,
          replies: []
        },
        {
          id: 3,
          author: 'Dr. Ahmed Hassan',
          authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel&id=18',
          date: 'May 18, 2025',
          text: 'The section on attachment and emotional well-being is crucial. How do you measure the long-term psychological impact of AI companion relationships?',
          likes: 18,
          replies: []
        }
      ]
    };
    
    return articleComments[slug] || [
      {
        id: 1,
        author: 'Blog Reader',
        authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel',
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        text: 'Great article! Looking forward to learning more about this topic.',
        likes: 5,
        replies: []
      }
    ];
  };

  // State for comments/discussion
  const [commentText, setCommentText] = React.useState('');
  const [replyText, setReplyText] = React.useState('');
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [comments, setComments] = React.useState([]);

  // Initialize comments when component mounts or slug changes
  React.useEffect(() => {
    setComments(getCommentsForArticle(slug));
  }, [slug]);
  
  // Remove unused useInView hook that's causing layout shifts
  // Instead, use a stable reference for the related articles section
  const relatedArticlesRef = React.useRef(null);

  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [slug]);

  // Check if article was previously bookmarked
  React.useEffect(() => {
    const bookmarked = localStorage.getItem(`bookmark-${slug}`);
    if (bookmarked) setIsBookmarked(true);
    
    const liked = localStorage.getItem(`like-${slug}`);
    if (liked) setHasLiked(true);
  }, [slug]);

  const toggleBookmark = () => {
    if (isBookmarked) {
      localStorage.removeItem(`bookmark-${slug}`);
    } else {
      localStorage.setItem(`bookmark-${slug}`, 'true');
    }
    setIsBookmarked(!isBookmarked);
  };

  const handleLike = () => {
    if (!hasLiked) {
      setLikeCount(prev => prev + 1);
      setHasLiked(true);
      localStorage.setItem(`like-${slug}`, 'true');
    } else {
      setLikeCount(prev => prev - 1);
      setHasLiked(false);
      localStorage.removeItem(`like-${slug}`);
    }
  };
  
  // Handle comment submission
  const handleCommentSubmit = () => {
    if (commentText.trim() === '') return;
    
    // Create new comment
    const newComment = {
      id: comments.length + 1,
      author: 'You',
      authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel',
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      text: commentText,
      likes: 0,
      replies: []
    };
    
    // Add to comments array
    setComments(prevComments => [newComment, ...prevComments]);
    
    // Clear input
    setCommentText('');
  };



  const currentPost = blogPosts[slug];
  
  // Get other blog post data - using useMemo to prevent re-creation on each render
  // Use an empty dependency array to ensure it only runs once and never updates
  const relatedPosts = React.useMemo(() => [
    {
      title: "The Future of AI Companions: Beyond Voice Assistants",
      slug: "future-ai-companions",
      excerpt: "Explore how AI companions like Ainous are evolving beyond voice assistants to become true partners in daily life, with embodied presence and emotional intelligence.",
      placeholderComponent: <CollectiveIndividualPlaceholder />,
      categories: ["AI", "Future Tech"]
    },
    {
      title: "How Embodied AI Changes Human-Computer Interaction",
      slug: "embodied-ai-hci",
      excerpt: "A deep dive into the psychology and design principles behind embodied AI and how physical presence transforms our relationship with technology.",
      placeholderComponent: <EmbodiedAIPlaceholder />,
      categories: ["Design", "Psychology"]
    },
    {
      title: "Designing for Trust: The UI of AI Companionship",
      slug: "designing-for-trust",
      excerpt: "An exploration of the unique UI/UX design challenges in creating interfaces that foster trust between humans and AI companions.",
      placeholderComponent: <UIDesignPlaceholder />,
      categories: ["Design", "UX"]
    }
  ], []); // Empty dependency array ensures this only runs once
  
  // Handle case where post doesn't exist
  if (!currentPost) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-3xl font-bold mb-4">Blog Post Not Found</h1>
          <p className="mb-6">The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blog" className="btn-primary">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Current page URL for sharing
  const pageUrl = `https://xdimension.ai/blog/ethics/${slug}`;
  
  // Share URLs
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(currentPost.title)}&url=${encodeURIComponent(pageUrl)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;

  // Create article data for schema
  const articleData = {
    title: currentPost.title,
    image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%234CAF50'/%3E%3C/svg%3E",
    publishedDate: currentPost.publishedDate,
    modifiedDate: currentPost.modifiedDate,
    url: pageUrl,
    description: currentPost.excerpt,
    author: {
      name: currentPost.author
    }
  };

  return (
    <div className="min-h-screen">
      <MetaTags 
        title={`${currentPost.title} | xdimension Blog`}
        description={currentPost.excerpt}
        image="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%234CAF50'/%3E%3C/svg%3E"
        url={pageUrl}
        article={true}
        publishedTime={currentPost.publishedDate}
        modifiedTime={currentPost.modifiedDate}
        tags={currentPost.tags}
      />
      <BlogPostSchema article={articleData} />

      {/* Reading progress bar */}
      <div 
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary-400 to-primary-600 z-50 transition-all duration-300"
        style={{ width: `${scrollProgress}%` }}
      ></div>
      
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-transform hover:translate-x-[-5px]">
            <FiArrowLeft className="mr-2" />
            Back to Blog
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="article-header"
          >
            {/* Categories */}
            <div className="article-categories">
              {currentPost.categories.map((category, idx) => (
                <span key={idx} className="article-category">
                  {category}
                </span>
              ))}
            </div>
            
            {/* Article Title */}
            <h1 className="article-title">
              {currentPost.title}
            </h1>
            
            {/* Article Meta */}
            <div className="article-meta">
              {/* Author */}
              <div className="article-author">
                <img src={currentPost.authorImage} alt={currentPost.author} className="article-author-image" />
                <div>
                  <div className="article-author-name">{currentPost.author}</div>
                  <div className="article-author-role">{currentPost.authorRole}</div>
                </div>
              </div>
              
              {/* Date */}
              <div className="article-date">
                <FiClock className="mr-1" />
                {currentPost.date}
              </div>
              
              {/* Tags */}
              <div className="article-tags">
                <FiTag className="mr-1" />
                {currentPost.tags.slice(0, 2).join(', ')}
                {currentPost.tags.length > 2 && '...'}
              </div>
            </div>
          </motion.div>

          {/* Featured image */}
          <div className="mb-10 rounded-xl overflow-hidden shadow-lg">
            {currentPost.placeholderComponent}
          </div>
          
          {/* Article Actions */}
          <div className="sticky top-4 float-right ml-6 mb-6 z-10 flex flex-col items-center gap-3 bg-white/80 backdrop-blur-sm p-3 rounded-full shadow-md">
            {/* Bookmark */}
            <button 
              onClick={toggleBookmark}
              className={`p-2 rounded-full transition-colors ${isBookmarked ? 'text-yellow-500 bg-yellow-50' : 'text-neutral-dark/70 hover:bg-neutral-100'}`}
              aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
            >
              <FiBookmark className={isBookmarked ? "fill-current" : ""} />
            </button>
            
            {/* Like */}
            <button 
              onClick={handleLike}
              className={`p-2 rounded-full transition-colors ${hasLiked ? 'text-red-500 bg-red-50' : 'text-neutral-dark/70 hover:bg-neutral-100'}`}
              aria-label={hasLiked ? "Unlike" : "Like"}
            >
              <FiThumbsUp className={hasLiked ? "fill-current" : ""} />
            </button>
            <span className="text-xs font-medium">{likeCount}</span>
            
            {/* Share */}
            <div className="relative group">
              <button className="p-2 rounded-full text-neutral-dark/70 hover:bg-neutral-100 transition-colors" aria-label="Share">
                <FiShare2 />
              </button>
              
              <div className="absolute right-full mr-2 top-0 bg-white shadow-lg rounded-lg p-2 w-max opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 flex flex-col gap-2">
                <ShareButton 
                  icon={<FiTwitter />} 
                  label="Twitter" 
                  url={twitterShareUrl} 
                  bgColor="bg-[#1DA1F2]"
                />
                <ShareButton 
                  icon={<FiFacebook />} 
                  label="Facebook" 
                  url={facebookShareUrl} 
                  bgColor="bg-[#1877F2]"
                />
                <ShareButton 
                  icon={<FiLinkedin />} 
                  label="LinkedIn" 
                  url={linkedinShareUrl} 
                  bgColor="bg-[#0A66C2]"
                />
              </div>
            </div>
          </div>
          
          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: `
                <p class="lead">${currentPost.excerpt}</p>
                ${currentPost.content}
              `
            }}
          />
          
          {/* Article Tags */}
          <div className="mt-12 mb-8">
            <h3 className="text-lg font-semibold mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {currentPost.tags.map((tag, idx) => (
                <span key={idx} className="px-3 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-dark/80 text-sm transition-colors cursor-pointer">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          
          {/* Comments Section */}
          <div className="my-16 border-t border-neutral-200 pt-12">
            {/* Discussion Section */}
            <div className="flex items-center gap-2 mb-8">
              <RiMessage2Line className="text-primary-600" size={24} />
              <h3 className="text-2xl font-display font-semibold">Discussion ({comments.length})</h3>
            </div>
            
            <div className="flex items-start gap-4 mb-12">
              <img 
                src="https://xsgames.co/randomusers/avatar.php?g=pixel"
                alt="Your profile" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1">
                <textarea 
                  className="w-full border border-neutral-200 rounded-lg p-4 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none transition-all resize-none h-32"
                  placeholder="Add to the discussion..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                ></textarea>
                <button 
                  className="mt-3 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                  onClick={handleCommentSubmit}
                >
                  Post Comment
                </button>
              </div>
            </div>
            
            {/* Comments List */}
            <div className="space-y-8 mb-16">
              {comments.map(comment => (
                <div key={comment.id} className="flex gap-4">
                  <img 
                    src={comment.authorImage} 
                    alt={comment.author} 
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{comment.author}</h4>
                      <span className="text-sm text-neutral-500">{comment.date}</span>
                    </div>
                    <p className="text-neutral-700 mb-2">{comment.text}</p>
                    <div className="flex items-center gap-2 text-sm text-neutral-500">
                      <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
                        <FiThumbsUp size={14} /> {comment.likes}
                      </button>
                      <span>•</span>
                      <button 
                        className="hover:text-primary-600 transition-colors"
                        onClick={() => setReplyingTo(comment.id)}
                      >
                        Reply
                      </button>
                    </div>
                    
                    {/* Show reply form if replying to this comment */}
                    {replyingTo === comment.id && (
                      <div className="flex items-start gap-3 mt-4">
                        <img 
                          src="https://xsgames.co/randomusers/avatar.php?g=pixel" 
                          alt="Your profile" 
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1">
                          <textarea 
                            className="w-full border border-neutral-200 rounded-lg p-3 focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none transition-all resize-none h-24 text-sm"
                            placeholder={`Reply to ${comment.author}...`}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                          ></textarea>
                          <div className="flex gap-2 mt-2">
                            <button 
                              className="px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm"
                              onClick={() => {
                                if (replyText.trim() === '') return;
                                
                                // Create new reply
                                const newReply = {
                                  id: Date.now(),
                                  author: 'You',
                                  authorImage: 'https://xsgames.co/randomusers/avatar.php?g=pixel',
                                  date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                                  text: replyText,
                                  likes: 0
                                };
                                
                                // Add reply to comment
                                setComments(prevComments => 
                                  prevComments.map(c => 
                                    c.id === comment.id 
                                      ? { ...c, replies: [...(c.replies || []), newReply] }
                                      : c
                                  )
                                );
                                
                                // Reset reply state
                                setReplyText('');
                                setReplyingTo(null);
                              }}
                            >
                              Post Reply
                            </button>
                            <button 
                              className="px-4 py-1.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 rounded-lg transition-colors text-sm"
                              onClick={() => {
                                setReplyText('');
                                setReplyingTo(null);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Show replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-6 mt-4 space-y-4 border-l-2 border-neutral-100 pl-4">
                        {comment.replies.map(reply => (
                          <div key={reply.id} className="flex gap-3">
                            <img 
                              src={reply.authorImage} 
                              alt={reply.author} 
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-sm">{reply.author}</h4>
                                <span className="text-xs text-neutral-500">{reply.date}</span>
                              </div>
                              <p className="text-neutral-700 text-sm">{reply.text}</p>
                              <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1">
                                <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
                                  <FiThumbsUp size={12} /> {reply.likes}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <div className="related-articles" ref={relatedArticlesRef}>
              <h2 className="related-articles-title">Related Articles</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((post, idx) => (
                  <div key={idx} className="related-article-card">
                    <div className="related-article-image">
                      {post.placeholderComponent}
                    </div>
                    <div className="related-article-content">
                      <div className="related-article-categories">
                        {post.categories.map((category, i) => (
                          <span key={i} className="related-article-category">
                            {category}
                          </span>
                        ))}
                      </div>
                      <h3 className="related-article-title">{post.title}</h3>
                      <p className="related-article-excerpt line-clamp-2">{post.excerpt}</p>
                      <Link to={`/blog/${post.slug}`} className="related-article-link">
                        Read Article <FiArrowRight />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPostEthics;